import type { CheckpointAnswer, CheckpointQuestion } from '@/utils/checkpoint.utils'

export type StoredImage = NonNullable<CheckpointAnswer['images']>[number]

export type QuestionAnswer = {
  value: string[]
  files: File[]
  comment?: string
  /** Already-uploaded images (present when editing an existing response). */
  existingImages?: StoredImage[]
}

/**
 * A `spare-parts` answer holds one value per listed part, encoded as
 * `<sparePartId>::<yes|no>::<label>`. The backend reads the id and the yes/no to
 * write the part's status; the label rides along last (so a label containing the
 * separator can't shift the other two) and keeps submissions readable in review.
 */
const SPARE_PART_ANSWER_SEPARATOR = '::'

export type SparePartAnswer = { sparePartId: string; used: boolean; label: string }

export const encodeSparePartAnswer = (sparePartId: string, used: boolean, label: string): string =>
  [sparePartId, used ? 'yes' : 'no', label].join(SPARE_PART_ANSWER_SEPARATOR)

export const decodeSparePartAnswers = (values: string[]): SparePartAnswer[] =>
  values.flatMap(value => {
    const [sparePartId, used, ...labelParts] = value.split(SPARE_PART_ANSWER_SEPARATOR)
    if (!sparePartId || (used !== 'yes' && used !== 'no')) return []
    return [
      {
        sparePartId,
        used: used === 'yes',
        label: labelParts.join(SPARE_PART_ANSWER_SEPARATOR),
      },
    ]
  })

export const isAnswerComplete = (
  question: CheckpointQuestion,
  answer: QuestionAnswer,
  /** Number of parts a `spare-parts` question lists — every one must be answered. */
  sparePartCount = 0,
): boolean => {
  if (question.type === 'group') return true
  if (question.type === 'spare-parts')
    return !question.required || answer.value.length >= sparePartCount
  if (!question.required) return true
  if (question.type === 'images')
    return answer.files.length > 0 || (answer.existingImages?.length ?? 0) > 0
  if (question.type === 'text-area') return (answer.value[0] ?? '').trim().length > 0
  return answer.value.length > 0
}

const createDevDefaultImageFile = (): File => {
  const canvas = document.createElement('canvas')
  canvas.width = 300
  canvas.height = 200
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#e5e5e5'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#dc2626'
  ctx.font = 'bold 56px sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('DEV', canvas.width / 2, canvas.height / 2)

  const base64 = canvas.toDataURL('image/png').split(',')[1]!
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
  return new File([bytes], 'dev-default.png', { type: 'image/png' })
}

/** Dev/localhost only — pre-fills a required question so the checkpoint dialog is
 * immediately submittable during manual testing. Never used in production. */
export const getDevDefaultAnswer = (question: CheckpointQuestion): QuestionAnswer | undefined => {
  if (!question.required) return undefined
  if (question.type === 'images') return { value: [], files: [createDevDefaultImageFile()] }
  if (question.type === 'text-area') return { value: ['Dev default answer'], files: [] }
  if (question.type === 'group') return undefined
  const options = question.options ?? []
  const option = options.find(o => !o.isNotRelevant) ?? options[0]
  return option ? { value: [option.value], files: [] } : undefined
}
